import React, { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { saveVolunteerTeam } from '@/data/tasks';
import Toast from 'react-native-toast-message';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import InputSection from '@/components/General/inputs/InputSection';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { Query } from '@/utils/react-query/Queries';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import SortableList from '@/components/General/SortableList';
import SolidButton from '@/components/General/SolidButton';
import Toggle from '@/components/General/inputs/Toggle';
import { isFollowUpDueType } from '@/utils/constants/taskTypes';

const EMPTY_DRAFT = {};

export default function AddTaskTeamModal() {
    const { id, fromScreen, fromStoreKey } = useLocalSearchParams()
    const storeKey = id || "new"

    const queryClient = useQueryClient();
    const router = useRouter();

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const team = useEditScreenStore(s => s.AddTaskTeamModal?.[storeKey]) || EMPTY_DRAFT

    const {data: teamToEdit} = Query.taskTeams.byId({id: id, options: {enabled: !!id}})

    // Follow Up care-task definitions are managed only through the
    // follow_up_*_task RPCs — they can never belong to a volunteer team, so
    // the picker filters them out (also keeps care def titles/descriptions
    // off this screen for non-team viewers, spec §8.4).
    const teamTasksQuery = Query.tasks.all();
    const attachableTasksResult = {
        ...teamTasksQuery,
        data: (teamTasksQuery.data || []).filter(t => !isFollowUpDueType(t?.dueType)),
    };

    const saveTeamMutation = useMutation({
        mutationFn: saveVolunteerTeam,
        onSuccess: (data) => {
            queryClient.invalidateQueries({queryKey: QueryKeys.taskTeams.all()});
            if(data?.id || id) queryClient.invalidateQueries({queryKey: QueryKeys.taskTeams.byId(data?.id || id)});
            queryClient.invalidateQueries({queryKey: QueryKeys.tasks.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.eventInstances.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.events.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.tasks.upcomingTasks()});
            queryClient.invalidateQueries({queryKey: QueryKeys.tasks.upcomingTaskAssignments()});
            queryClient.invalidateQueries({queryKey: ['dashboardData']});
            Toast.show({ type: "success", text2: "Task Team Saved Successfully!" });
            removeEditScreenData("AddTaskTeamModal", storeKey)

            if(fromScreen){
                useEditScreenStore.getState().dataChangeFunction?.(data)
                router.dismissTo({pathname:fromScreen, params: fromStoreKey && fromStoreKey != "new" ? {id: fromStoreKey} : undefined});
            }
            else{
                redirectToDefault(router, "AddTaskTeamModal")
            }
        }
    })

    const [reorderOpen, setReorderOpen] = useState(false)

    // Only offer propagating to future events when the task set actually
    // differs from the original set (task added or removed).
    const currentTaskIDs = (team.Tasks || []).map(t => t.id).sort().join('|')
    const originalTaskIDs = (team.originalValues?.Tasks || []).map(t => t.id).sort().join('|')
    const tasksChanged = currentTaskIDs !== originalTaskIDs

    const setTeam = (details) => {
        setEditScreenData("AddTaskTeamModal", storeKey, details)
    }

    useEffect(() => {
        if(teamToEdit?.id && Object.keys(team).length === 0){
            setTeam({
                ...teamToEdit,
                originalValues: teamToEdit,
            })
        }
    }, [teamToEdit])

    // Merge selected tasks with existing ones to preserve their assigneesNeeded value
    const handleTasksChange = (selected_items) => {
        const existing = team.Tasks || []
        const merged = selected_items.map(sel => {
            const prev = existing.find(t => t.id === sel.id)
            return { ...sel, assigneesNeeded: prev?.assigneesNeeded ?? sel.assigneesNeeded ?? 1 }
        })
        setTeam({ Tasks: merged })
    }

    const updateTaskAssigneesNeeded = (taskID, text) => {
        const numericValue = parseInt(text)
        const valid = text === "" || (!isNaN(numericValue) && numericValue >= 1 && numericValue <= 99)
        if(!valid) return
        setTeam({
            Tasks: (team.Tasks || []).map(t => t.id === taskID ? { ...t, assigneesNeeded: text === "" ? "" : numericValue } : t)
        })
    }

    const handleSave = () => {
        if(!team.name?.trim()){
            Toast.show({ type: "error", text2: "Please enter a team name." })
            return
        }
        if(!team.Assigners || team.Assigners.length === 0){
            Toast.show({ type: "error", text2: "Please select at least one assigner." })
            return
        }
        const { originalValues, originalEventIDs, ...cleanTeam } = team;
        saveTeamMutation.mutate({...cleanTeam, originalValues, originalEventIDs: originalEventIDs || []})
    }

    return (
        <ModalWrapper
            title={id ? "Edit Task Team" : "New Task Team"}
            onConfirm={handleSave}
        >
        
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled">
                <View style={styles.paddingBottom}>
                    <InputSection>
                        <Input
                            title="Name"
                            autoCapitalize="words"
                            value={team.name}
                            onUpdate={(new_value) => {
                                setTeam({name: new_value})
                            }}
                        />

                        <Input
                            title="Description"
                            multiline
                            value={team.description}
                            onUpdate={(new_value) => {
                                setTeam({description: new_value})
                            }}
                        />

                        <SheetSelector type="people" title="Assigners" required helpText="People who can assign tasks for this team. Self-assignment and forwarding still work when enabled on individual tasks." fromScreen={`tasks/teams/create`} fromStoreKey={storeKey} multiple value={team.Assigners || []} onChange={(selected_items) => {
                            setTeam({ Assigners: selected_items})
                        }} />

                        <SheetSelector type="roles" title="Member Role" helpText="Members of the selected role are this team's members — they'll show first when assigning tasks and count as team members on the dashboard." fromScreen={`tasks/teams/create`} fromStoreKey={storeKey} value={team.Role} onChange={(selected) => {
                            setTeam({ Role: selected ?? null })
                        }} />

                        <SheetSelector type="tasks" title="Tasks" helpText="Tasks that belong to this team. Set how many assignees each task needs below." fromScreen={`tasks/teams/create`} fromStoreKey={storeKey} multiple queryResult={attachableTasksResult} value={team.Tasks || []} onChange={handleTasksChange} />

                        <SheetSelector type="events" title="Events" helpText="Add this team's tasks to all future instances of selected events. On edit, adding or removing events will sync this team to future instances accordingly." fromScreen={`tasks/teams/create`} fromStoreKey={storeKey} multiple value={team.Events || []} onChange={(selected_events) => {
                            setTeam({ Events: selected_events })
                        }} />

                        {id && team.Tasks?.length > 0 && tasksChanged && (
                            <View style={styles.propagateSection}>
                                <View style={styles.propagateRow}>
                                    <Text style={styles.propagateLabel}>Propagate changes to future events</Text>
                                    <Toggle
                                        onValueChange={() => setTeam({ propagateToFutureEvents: !team.propagateToFutureEvents })}
                                        value={team.propagateToFutureEvents || false}
                                    />
                                </View>
                                <Text style={styles.propagateDescription}>Add new tasks to and remove dropped tasks from all upcoming event instances for this team.</Text>
                            </View>
                        )}

                        {team.Tasks?.length > 0 && (
                            <View style={styles.tasksConfig}>
                                <View style={styles.tasksConfigHeader}>
                                    <Text style={styles.tasksConfigTitle}>Assignees Needed Per Task</Text>
                                    {team.Tasks.length > 1 && (
                                        <SolidButton
                                            text="Reorder"
                                            iconName="reorder-four-outline"
                                            iconBefore
                                            size="sm"
                                            variant="muted"
                                            type="primary"
                                            onPress={() => setReorderOpen(true)}
                                        />
                                    )}
                                </View>

                                {team.Tasks.map((task, index) => (
                                    <View key={task.id} style={[styles.taskRow, index < team.Tasks.length - 1 && styles.taskRowBordered]}>
                                        <View style={styles.taskRowInfo}>
                                            <Text style={styles.taskRowPosition}>{index + 1}</Text>
                                            <Text style={styles.taskRowTitle} numberOfLines={1}>{task.title}</Text>
                                        </View>
                                        <View style={styles.taskRowInput}>
                                            <Input
                                                title=""
                                                placeholder="1"
                                                type="numeric"
                                                value={task.assigneesNeeded ? String(task.assigneesNeeded) : ""}
                                                onUpdate={(text) => updateTaskAssigneesNeeded(task.id, text)}
                                            />
                                        </View>
                                    </View>
                                ))}
                            </View>
                        )}

                    </InputSection>
                </View>
            </KeyboardAwareScrollView>

            <SortableList
                items={team.Tasks || []}
                isOpen={reorderOpen}
                close={() => setReorderOpen(false)}
                onConfirm={(sorted) => setTeam({ Tasks: sorted })}
                title="Reorder Tasks"
                helpText="Drag tasks to set their order within this team."
                itemContent={(item) => <Text style={styles.reorderItemText} numberOfLines={1}>{item.title}</Text>}
            />
        </ModalWrapper> 
    );
}


const styles = StyleSheet.create(({colors, fonts}) => ({
    anyChosenTasksText: {
        marginTop: 5, 
        color:colors.textSecondary, 
        fontSize:11, 
        fontStyle:"italic"
    },
    paddingBottom: {
        paddingBottom:20
    },
    tasksConfig: {
        marginTop: 4,
        marginBottom: 8,
    },
    tasksConfigHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 8,
    },
    tasksConfigTitle: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
    },
    taskRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        paddingVertical: 4,
    },
    taskRowBordered: {
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        paddingBottom: 8,
        marginBottom: 4,
    },
    taskRowInfo: {
        flex: 1,
        flexDirection: "row",
        alignItems: "center",
        gap: 10,
    },
    taskRowPosition: {
        color: colors.textSecondary,
        fontFamily: fonts.heading,
        fontSize: 13,
        minWidth: 16,
        textAlign: "center",
    },
    taskRowTitle: {
        flex: 1,
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: 14,
    },
    taskRowInput: {
        width: 100,
    },
    reorderItemText: {
        color: colors.text,
        fontFamily: fonts.body,
        fontSize: 14,
    },
    propagateSection: {
        marginVertical: 12,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
    },
    propagateRow: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
    },
    propagateLabel: {
        color: colors.text,
        fontSize: 15,
        fontFamily: fonts.body,
        flex: 1,
    },
    propagateDescription: {
        fontStyle: "italic",
        fontSize: 12,
        paddingTop: 4,
        color: colors.textSecondary,
    },
}))
